// src/pages/scheduler/SchedulerDetail.tsx — trigger detail view.
// Port of the prototype scheduler-detail.jsx, wired to the live API: enable
// toggle + schedule edits go through onPatch; Run now calls onRunNow; the run
// history is fetched lazily from /api/scheduler/triggers/:id/history.

import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { C, F } from "../../theme";
import * as Icons from "../../components/ui/Icon";
import { apiFetch, ApiError } from "../../api";
import { Eyebrow, Pill, Stamp, StatusChip, Toggle, Segmented, iconByKey } from "./kit";
import { ConfigControl } from "./controls";
import {
  DOW, SEV, TILE, scheduleSummary, formatRunAt, formatDuration,
  type ConfigControl as Ctl, type RunRecord, type Schedule, type SchedTrigger,
} from "./types";

export interface DetailPatch { enabled?: boolean; schedule?: Schedule; config?: Ctl[] }

export function Block({ icon, eyebrow, title, children, right, accent = C.brassDk }: { icon?: string; eyebrow?: string; title: string; children: ReactNode; right?: ReactNode; accent?: string }) {
  const Ic = icon ? iconByKey(icon) : null;
  return (
    <section style={{ background: C.card, borderRadius: 18, border: `1px solid ${C.hairlineLt}`, boxShadow: C.shadowS, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "14px 16px 12px", borderBottom: `1px solid ${C.hairlineLt}` }}>
        {Ic && <div style={{ width: 34, height: 34, borderRadius: 10, background: C.paperWarm, border: `1px solid ${C.hairlineLt}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Ic s={18} c={accent} /></div>}
        <div style={{ flex: 1, minWidth: 0 }}>
          {eyebrow && <Eyebrow c={accent} style={{ fontSize: 12 }}>{eyebrow}</Eyebrow>}
          <div style={{ fontFamily: F.serif, fontSize: 17, fontWeight: 700, color: C.ink, letterSpacing: -0.2, lineHeight: 1.1 }}>{title}</div>
        </div>
        {right}
      </div>
      <div style={{ padding: "4px 16px 16px" }}>{children}</div>
    </section>
  );
}

const DAY_LABEL: Record<string, string> = { mon: "M", tue: "T", wed: "W", thu: "T", fri: "F", sat: "S", sun: "S" };
const TYPE_OPTS = [
  { value: "interval" as const, label: "Interval" },
  { value: "time-of-day" as const, label: "Time of day" },
  { value: "on-event" as const, label: "On event" },
];

function ScheduleEditor({ sched, onChange }: { sched: Schedule; onChange: (s: Schedule) => void }) {
  const setType = (t: "interval" | "time-of-day" | "on-event") => {
    if (t === sched.type) return;
    if (t === "interval") onChange({ type: "interval", interval: { every: 5, unit: "minutes" } });
    else if (t === "time-of-day") onChange({ type: "time-of-day", times: ["09:00"], days: [...DOW] });
    else onChange({ type: "on-event", event: "manual", eventLabel: "Manual / app-fired" });
  };
  return (
    <div>
      <Segmented value={sched.type} options={TYPE_OPTS} onChange={setType} />
      <div style={{ marginTop: 14 }}>
        {sched.type === "interval" && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 14, color: C.inkSoft, fontWeight: 600 }}>Run every</span>
            <input type="number" min={1} value={sched.interval.every} onChange={(e) => onChange({ type: "interval", interval: { ...sched.interval, every: parseInt(e.target.value) || 1 } })}
              style={{ width: 64, textAlign: "center", padding: "9px 6px", borderRadius: 10, border: `1px solid ${C.hairline}`, background: C.paperSoft, fontFamily: F.mono, fontSize: 17, fontWeight: 700, color: C.ink, outline: "none" }} />
            <Segmented value={sched.interval.unit} options={(["minutes", "hours", "days"] as const).map((u) => ({ value: u, label: u }))} onChange={(u) => onChange({ type: "interval", interval: { ...sched.interval, unit: u } })} />
          </div>
        )}
        {sched.type === "time-of-day" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "#2B2417", borderRadius: 12, padding: "8px 14px", border: `1px solid ${C.brassDk}`, alignSelf: "flex-start" }}>
              <Icons.Clock s={18} c={C.brassGlow} />
              <input type="time" value={(sched.times || ["09:00"])[0]} onChange={(e) => onChange({ ...sched, times: [e.target.value] })}
                style={{ border: "none", outline: "none", background: "transparent", fontFamily: F.mono, fontSize: 22, fontWeight: 700, color: C.brassGlow, letterSpacing: 1, colorScheme: "dark" }} />
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              {DOW.map((d) => {
                const on = (sched.days || []).includes(d);
                return (
                  <button key={d} type="button" onClick={() => onChange({ ...sched, days: on ? (sched.days || []).filter((x) => x !== d) : [...DOW].filter((x) => (sched.days || []).includes(x) || x === d) })}
                    style={{ width: 40, height: 40, borderRadius: 11, cursor: "pointer", border: `1px solid ${on ? C.brassDk : C.hairline}`, background: on ? C.brassDk : C.card, color: on ? C.brassGlow : C.inkMute, fontFamily: F.body, fontWeight: 800, fontSize: 13.5, boxShadow: on ? "none" : C.shadowS }}>{DAY_LABEL[d]}</button>
                );
              })}
            </div>
          </div>
        )}
        {sched.type === "on-event" && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, background: C.heatherBg, border: "1px solid rgba(122,93,140,0.22)", borderRadius: 12, padding: "12px 14px" }}>
            <Icons.Lightning s={18} c={C.heatherDk} />
            <div>
              <div style={{ fontFamily: F.body, fontSize: 14, fontWeight: 800, color: C.heatherDk }}>{sched.eventLabel || sched.event || "App event"}</div>
              <div style={{ fontSize: 12, color: C.inkSoft, fontWeight: 600, marginTop: 1 }}>Fired by the owning app, not on a clock.</div>
            </div>
          </div>
        )}
      </div>
      <div style={{ marginTop: 14, padding: "10px 12px", borderRadius: 10, background: C.paperWarm, border: `1px solid ${C.hairlineLt}`, display: "flex", alignItems: "center", gap: 9 }}>
        <Icons.Info s={15} c={C.inkMute} />
        <span style={{ fontSize: 12.5, color: C.inkSoft, fontWeight: 600 }}>Runs <b style={{ color: C.brassDk }}>{scheduleSummary(sched)}</b>. The heartbeat works out the timing automatically.</span>
      </div>
    </div>
  );
}

function RunHistory({ history }: { history: RunRecord[] | null }) {
  if (history === null) return <div style={{ padding: "14px 0", fontFamily: F.body, fontSize: 13, color: C.inkMute, fontWeight: 600 }}>Loading history…</div>;
  if (history.length === 0) return <div style={{ padding: "18px 0", textAlign: "center", fontFamily: F.serif, fontStyle: "italic", color: C.inkMute }}>No runs recorded yet.</div>;
  return (
    <div>
      {history.map((h, i) => {
        const dot = h.status === "success" ? C.sage : h.status === "failed" ? C.signal : h.status === "running" ? C.brass : C.inkMute;
        return (
          <div key={i} style={{ display: "flex", gap: 12, padding: "11px 0", borderTop: i === 0 ? "none" : `1px solid ${C.hairlineLt}`, alignItems: "flex-start" }}>
            <span style={{ width: 9, height: 9, borderRadius: 999, background: dot, marginTop: 5, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontFamily: F.mono, fontSize: 12.5, fontWeight: 700, color: C.ink }}>{formatRunAt(h.at)}</span>
                <StatusChip status={h.status} small />
              </div>
              <div style={{ fontSize: 13, color: C.inkSoft, fontWeight: 600, marginTop: 3, lineHeight: 1.4 }}>{h.message}</div>
            </div>
            <span style={{ fontFamily: F.mono, fontSize: 11.5, color: C.inkMute, fontWeight: 700, flexShrink: 0, marginTop: 2 }}>{formatDuration(h.durationMs)}</span>
          </div>
        );
      })}
    </div>
  );
}

export function TriggerDetail({ trig, app, onPatch, onRunNow }: {
  trig: SchedTrigger;
  app: { name: string; icon: string; tone: string } | undefined;
  onPatch: (p: DetailPatch) => void;
  onRunNow: () => Promise<RunRecord | null>;
}) {
  const t = TILE[app?.tone ?? "brass"] || TILE.brass;
  const AppIc = iconByKey(app?.icon);
  const sev = SEV[trig.impact.severity] || SEV.low;
  const external = trig.managed === "external";
  const [flash, setFlash] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [history, setHistory] = useState<RunRecord[] | null>(null);

  const loadHistory = () => {
    setHistory(null);
    apiFetch<{ history: RunRecord[] }>(`/api/scheduler/triggers/${encodeURIComponent(trig.id)}/history`)
      .then((r) => setHistory(r.history))
      .catch(() => setHistory([]));
  };
  useEffect(loadHistory, [trig.id]);

  const runNow = async () => {
    setRunning(true);
    try {
      const lr = await onRunNow();
      setFlash(lr ? `Ran: ${lr.message}` : "Run queued.");
      loadHistory();
    } catch (e) {
      setFlash(e instanceof ApiError ? e.message : "Run failed.");
    } finally {
      setRunning(false);
      window.setTimeout(() => setFlash(null), 3200);
    }
  };

  const setConfig = (key: string, value: unknown) => onPatch({ config: trig.config.map((c) => (c.key === key ? { ...c, value } : c)) });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* HERO */}
      <section style={{ background: C.card, borderRadius: 18, border: `1px solid ${C.hairlineLt}`, boxShadow: C.shadowM, overflow: "hidden" }}>
        <div style={{ height: 3, background: `linear-gradient(90deg,${t.ic},transparent 75%)` }} />
        <div style={{ padding: "16px 18px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 12, flexWrap: "wrap" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "5px 11px 5px 7px", borderRadius: 999, background: t.bg, border: `1px solid ${C.hairlineLt}`, whiteSpace: "nowrap" }}>
              <AppIc s={15} c={t.ic} />
              <span style={{ fontFamily: F.body, fontSize: 12.5, fontWeight: 800, color: t.ic, letterSpacing: 0.2 }}>{app?.name ?? trig.app}</span>
            </span>
            <Pill tone={sev.tone} small>{sev.label}</Pill>
            <span style={{ flex: 1 }} />
            <Stamp tone="paper">v{trig.version}</Stamp>
          </div>
          <h1 style={{ fontFamily: F.serif, fontSize: 27, fontWeight: 700, color: C.ink, margin: 0, letterSpacing: -0.5, lineHeight: 1.08 }}>{trig.name}</h1>
          <p style={{ fontSize: 14.5, color: C.inkSoft, fontWeight: 600, margin: "8px 0 0", lineHeight: 1.5, maxWidth: 560 }}>{trig.summary}</p>

          {external ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 14px", borderRadius: 13, background: C.sageBg, border: `1px solid ${C.sageDk}33`, fontFamily: F.body, fontWeight: 800, fontSize: 14, color: C.sageDk }}>
                <Icons.Cloud s={16} c={C.sageDk} /> External · Azure Logic App
              </span>
              <span style={{ fontSize: 12.5, color: C.inkMute, fontWeight: 700 }}>Runs <b style={{ color: C.sageDk }}>{trig.cadence}</b> · read-only here</span>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
              <div onClick={() => onPatch({ enabled: !trig.enabled })} role="button" tabIndex={0} style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: "9px 14px 9px 11px", borderRadius: 13, cursor: "pointer", border: `1px solid ${trig.enabled ? C.sageDk : C.hairline}`, background: trig.enabled ? C.sageBg : C.card, boxShadow: C.shadowS }}>
                <Toggle on={trig.enabled} onToggle={() => onPatch({ enabled: !trig.enabled })} size={0.86} />
                <span style={{ fontFamily: F.body, fontSize: 14, fontWeight: 800, color: trig.enabled ? C.sageDk : C.inkMute }}>{trig.enabled ? "Enabled" : "Disabled"}</span>
              </div>
              <button type="button" onClick={runNow} disabled={running} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "11px 16px", borderRadius: 13, cursor: "pointer", whiteSpace: "nowrap", background: "#2B2417", border: `1px solid ${C.brassDk}`, color: C.brassGlow, fontFamily: F.body, fontWeight: 800, fontSize: 14, letterSpacing: 0.2, boxShadow: "inset 0 1px 0 rgba(243,220,154,0.18)", opacity: running ? 0.7 : 1 }}>
                <Icons.Play s={15} c={C.brassGlow} /> {running ? "Running…" : "Run now"}
              </button>
              <div style={{ flex: 1, minWidth: 120, textAlign: "right" }}>
                {trig.enabled
                  ? <span style={{ fontSize: 12.5, color: C.inkMute, fontWeight: 700 }}>Next run <b style={{ fontFamily: F.mono, color: C.brassDk }}>{trig.nextRun}</b></span>
                  : <StatusChip status="paused" small />}
              </div>
            </div>
          )}
          {flash && <div style={{ marginTop: 12, padding: "10px 13px", borderRadius: 11, background: C.sageBg, border: `1px solid ${C.sage}55`, display: "flex", alignItems: "center", gap: 9, fontFamily: F.body, fontSize: 13, fontWeight: 700, color: C.sageDk }}><Icons.Check s={15} c={C.sageDk} />{flash}</div>}
        </div>
      </section>

      <Block icon="Info" eyebrow="What it does" title="About this trigger">
        <p style={{ fontSize: 14.5, color: C.inkSoft, fontWeight: 600, lineHeight: 1.6, margin: "6px 0 0" }}>{trig.description}</p>
        <div style={{ display: "flex", gap: 10, marginTop: 14, padding: "11px 13px", borderRadius: 11, background: C.paperWarm, border: `1px solid ${C.hairlineLt}`, alignItems: "flex-start" }}>
          <Icons.Lightning s={16} c={C.brassDk} style={{ marginTop: 1, flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 11, color: C.inkMute, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase" }}>Mechanism</div>
            <div style={{ fontSize: 13.5, color: C.inkSoft, fontWeight: 700, marginTop: 2, lineHeight: 1.45 }}>{trig.does}</div>
          </div>
        </div>
      </Block>

      <Block icon="Clock" eyebrow="When it runs" title="Schedule" right={<Stamp tone="brass">{external ? trig.cadence : scheduleSummary(trig.schedule)}</Stamp>}>
        <div style={{ paddingTop: 10 }}>
          {external ? (
            <div style={{ padding: "10px 12px", borderRadius: 10, background: C.paperWarm, border: `1px solid ${C.hairlineLt}`, display: "flex", alignItems: "center", gap: 9 }}>
              <Icons.Info s={15} c={C.inkMute} />
              <span style={{ fontSize: 12.5, color: C.inkSoft, fontWeight: 600 }}>Runs <b style={{ color: C.sageDk }}>{trig.cadence}</b>. Managed in Azure (logic-nymr-infohub-monitor) — change the schedule there, not here.</span>
            </div>
          ) : (
            <ScheduleEditor sched={trig.schedule} onChange={(s) => onPatch({ schedule: s })} />
          )}
        </div>
      </Block>

      <Block icon="Sliders" eyebrow="Tunable by you" title="Configuration" accent={C.heatherDk} right={<Stamp tone="heather">{trig.config.length} controls</Stamp>}>
        {trig.config.length === 0
          ? <div style={{ padding: "18px 0", textAlign: "center", fontFamily: F.serif, fontStyle: "italic", color: C.inkMute }}>No extra settings — tune the schedule above, or the thresholds in System Preferences.</div>
          : <div style={{ marginTop: 2 }}>{trig.config.map((item) => <ConfigControl key={item.key} item={item} onChange={(v) => setConfig(item.key, v)} />)}</div>}
      </Block>

      <Block icon="Target" eyebrow="Who & what is impacted" title="Impact" accent={C.signalDk} right={<Pill tone={sev.tone} small>{sev.label}</Pill>}>
        <div style={{ paddingTop: 8, display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: C.inkMute, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 4 }}>Audience</div>
            <div style={{ fontSize: 14.5, color: C.ink, fontWeight: 700, lineHeight: 1.4 }}>{trig.impact.audience}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: C.inkMute, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>Systems touched</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {trig.impact.systems.map((s) => <span key={s} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 11px", borderRadius: 999, background: C.paperWarm, border: `1px solid ${C.hairlineLt}`, fontFamily: F.body, fontSize: 12.5, fontWeight: 700, color: C.inkSoft }}><Icons.Database s={12} c={C.inkMute} />{s}</span>)}
            </div>
          </div>
          {trig.impact.notes && (
            <div style={{ display: "flex", gap: 10, padding: "11px 13px", borderRadius: 11, background: C.signalBg, border: `1px solid ${C.signal}33`, alignItems: "flex-start" }}>
              <Icons.Alert s={16} c={C.signalDk} style={{ marginTop: 1, flexShrink: 0 }} />
              <div style={{ fontSize: 13, color: C.signalDk, fontWeight: 700, lineHeight: 1.45 }}>{trig.impact.notes}</div>
            </div>
          )}
        </div>
      </Block>

      <Block icon="History" eyebrow="Recent activity" title="Run history" right={<StatusChip status={trig.enabled ? (trig.lastRun?.status ?? "never") : "paused"} small />}>
        <RunHistory history={history} />
      </Block>

      <Block icon="Hash" eyebrow="Provenance" title="Version & history">
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0 14px", borderBottom: `1px solid ${C.hairlineLt}` }}>
          <div style={{ fontFamily: F.mono, fontSize: 26, fontWeight: 700, color: C.brassDk, letterSpacing: -0.5 }}>v{trig.version}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, color: C.ink, fontWeight: 700 }}>Updated {trig.updatedAt}</div>
            <div style={{ fontSize: 12.5, color: C.inkMute, fontWeight: 600 }}>by {trig.updatedBy}</div>
          </div>
        </div>
        <div style={{ paddingTop: 6 }}>
          {trig.changelog.map((c, i) => (
            <div key={i} style={{ display: "flex", gap: 12, padding: "9px 0", borderTop: i === 0 ? "none" : `1px solid ${C.hairlineLt}` }}>
              <Stamp tone="paper" style={{ flexShrink: 0, height: "fit-content" }}>v{c.v}</Stamp>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, color: C.inkSoft, fontWeight: 600, lineHeight: 1.45 }}>{c.note}</div>
                <div style={{ fontFamily: F.mono, fontSize: 11, color: C.inkMute, fontWeight: 600, marginTop: 2 }}>{c.at}</div>
              </div>
            </div>
          ))}
        </div>
      </Block>

      <div style={{ textAlign: "center", padding: "4px 0 8px" }}>
        <code style={{ fontFamily: F.mono, fontSize: 11.5, color: C.inkMute, fontWeight: 600, background: C.paperWarm, padding: "5px 10px", borderRadius: 7, border: `1px solid ${C.hairlineLt}` }}>id: {trig.id}</code>
      </div>
    </div>
  );
}
